/**
 * briefing-api.ts — tolerant, feature-detected client for the Heute-Briefing
 * Entscheid counts (Story UI-U10).
 *
 * `GET /agent/briefing` returns the shared `PendingWorkSummary` (tasks / renewals /
 * invoices). It is a small allowed read added by this story, but the Briefing must
 * still degrade gracefully if the endpoint is absent (older deploy) or returns an
 * unexpected shape. So this module:
 *   - coerces every count to a finite, non-negative integer,
 *   - accepts a partial payload (missing sections default to zero/empty),
 *   - returns `null` on anything that is not a recognisable summary object,
 *   - swallows ANY fetch/HTTP error to `null` (404 = endpoint absent).
 * Result: a null summary → the Briefing renders tiles WITHOUT counts, never a crash.
 *
 * [Source: apps/web/src/features/communication-v2/api/comm-assist-api.ts] (tolerant-client precedent)
 * [Source: apps/web/src/lib/api-client.ts#apiFetch] (throws on !response.ok; 204 → undefined)
 * [Source: docs/stories/ui-u10-brok-briefing-ray.md AC4]
 */

import type { PendingWorkSummary } from '@brokly/shared';
import { apiFetch } from '../../../lib/api-client';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Finite, non-negative integer count — anything else (NaN, negative, string) → 0. */
function toCount(v: unknown): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

/** Array pass-through (typed as the summary's item arrays), else []. */
function asItems<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

/**
 * Parse a `GET /agent/briefing` payload into a `PendingWorkSummary`, or `null` when
 * the shape carries none of the recognised sections (tasks / renewals / invoices).
 */
export function parseBriefing(raw: unknown): PendingWorkSummary | null {
  if (!isPlainObject(raw)) return null;

  const tasks = isPlainObject(raw['tasks']) ? raw['tasks'] : undefined;
  const renewals = isPlainObject(raw['renewals']) ? raw['renewals'] : undefined;
  const invoices = isPlainObject(raw['invoices']) ? raw['invoices'] : undefined;

  // Unrecognised shape → hide (feature-detect). A real summary has at least one section.
  if (!tasks && !renewals && !invoices) return null;

  return {
    tasks: {
      count: toCount(tasks?.['count']),
      items: asItems<PendingWorkSummary['tasks']['items'][number]>(tasks?.['items']),
    },
    renewals: {
      red: toCount(renewals?.['red']),
      orange: toCount(renewals?.['orange']),
      items: asItems<PendingWorkSummary['renewals']['items'][number]>(renewals?.['items']),
    },
    invoices: {
      awaitingCount: toCount(invoices?.['awaitingCount']),
    },
  };
}

/**
 * Feature-detect + fetch the briefing summary. Returns null on ANY failure
 * (absent endpoint → apiFetch throws on 404; 204 → undefined → null).
 */
export async function fetchBriefing(): Promise<PendingWorkSummary | null> {
  try {
    return parseBriefing(await apiFetch<unknown>('/agent/briefing'));
  } catch {
    return null;
  }
}

/**
 * Mock-mode briefing — static counts mirroring Sebastian's prototype tiles
 * (Rechnungen 8 · Renewals 4 · Aufgaben 3). The Eingang tile has no summary field.
 */
export function deriveMockBriefing(): PendingWorkSummary {
  return {
    tasks: { count: 3, items: [] },
    renewals: { red: 4, orange: 6, items: [] },
    invoices: { awaitingCount: 8 },
  };
}
